import _Vue from 'vue';
import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError
} from '@azure/msal-browser';

export interface MsalReactiveData {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  idToken: string | null;
  graph: {
    groups?: {
      value: Array<{ id: string }>;
    };
  };
}

const LOGIN_SCOPES = ['openid', 'profile'];

export default {
  install(Vue: typeof _Vue, options: Configuration): void {
    const publicClientApp = new PublicClientApplication(options);

    const reactiveData: MsalReactiveData = Vue.observable({
      isAuthenticated: false,
      user: null,
      idToken: null,
      graph: {}
    });

    function setAuthenticatedState(result: AuthenticationResult): void {
      reactiveData.idToken = result.idToken;
      reactiveData.isAuthenticated = true;
      reactiveData.user = result.account;
    }

    async function acquireToken(): Promise<void> {
      const account = publicClientApp.getActiveAccount();
      if (!account) {
        await publicClientApp.loginRedirect({ scopes: LOGIN_SCOPES });
        return;
      }
      try {
        const response = await publicClientApp.acquireTokenSilent({
          scopes: LOGIN_SCOPES,
          account
        });
        setAuthenticatedState(response);
        if (response.account) {
          extractGroupsFromAccount(response.account);
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          publicClientApp.acquireTokenRedirect({
            scopes: LOGIN_SCOPES
          });
        }
        // For other errors, keep the existing idToken (if any)
      }
    }

    function extractGroupsFromAccount(account: AccountInfo): void {
      const claims = account.idTokenClaims;
      if (claims?.groups && Array.isArray(claims.groups)) {
        reactiveData.graph = {
          groups: {
            value: claims.groups.map((id: string) => ({ id }))
          }
        };
      }
    }

    Vue.prototype.$msal = {
      data: reactiveData,

      async acquireToken(): Promise<void> {
        return acquireToken();
      },

      async signIn(): Promise<void> {
        const redirectResult = await publicClientApp.handleRedirectPromise();

        if (redirectResult?.account) {
          publicClientApp.setActiveAccount(redirectResult.account);
          setAuthenticatedState(redirectResult);
        }

        const accounts = publicClientApp.getAllAccounts();
        if (!publicClientApp.getActiveAccount() && accounts.length > 0) {
          publicClientApp.setActiveAccount(accounts[0]);
        }

        const account = publicClientApp.getActiveAccount();
        if (account) {
          if (!reactiveData.idToken) {
            await acquireToken();
          }
          extractGroupsFromAccount(account);
        } else {
          await publicClientApp.loginRedirect({
            scopes: LOGIN_SCOPES
          });
        }
      },

      signOut(): void {
        publicClientApp.logoutRedirect();
      }
    };
  }
};
